import { useState } from 'react';
import { api } from '../../api';
import type { MagentoOperationCheck } from '../../api';
import { useResource } from '../../hooks';
import { ConfirmDialog, ErrorNotice, LoadingRows, StatusBadge } from '../ui';
import { Callout, Section, Subsection } from '../primitives';
import { LevelBadge, QuietEmpty } from './ui';
import { relativeTime, sinceWords } from './projectHealth';

type Operation = 'reindex_all' | 'cache_clean' | 'cache_flush';

const operationText: Record<Operation, { title: string; label: string; description: string }> = {
  reindex_all: {
    title: 'Reindex every indexer?',
    label: 'Reindex all',
    description: 'Runs php bin/magento indexer:reindex in the live release. The store stays online; on a large catalog it takes a while and loads the database. It runs as a recorded operation you can follow.',
  },
  cache_clean: {
    title: 'Clean the Magento cache?',
    label: 'Clean cache',
    description: 'Runs php bin/magento cache:clean: outdated entries of every enabled cache type are removed. Pages are rebuilt as they are requested.',
  },
  cache_flush: {
    title: 'Flush the Magento cache storage?',
    label: 'Flush cache',
    description: 'Runs php bin/magento cache:flush: the whole cache storage is emptied, including entries other applications share. The next requests are slower while it refills. Prefer "Clean cache" unless cleaning did not help.',
  },
};

/**
 * MagentoOperationsPanel is the managed path's Magento operations (UX-09):
 * whether cron runs, whether an indexer needs a reindex, whether consumers
 * keep up, and reindex and cache operations — each run as a recorded
 * execution of the environment, never a silent command.
 */
export function MagentoOperationsPanel({ environmentId, onExecutionStarted }: { environmentId: string; onExecutionStarted: (executionId: string) => void }) {
  const view = useResource(() => api.getManagedMagentoOperations(environmentId), [environmentId], environmentId, { key: `env:${environmentId}:magento-operations` });
  const [confirm, setConfirm] = useState<Operation | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // The operation that failed, so Retry runs the same one again.
  const [failed, setFailed] = useState<Operation | null>(null);

  async function run(op: Operation) {
    setBusy(true);
    setError(null);
    setFailed(null);
    try {
      const res = await api.runManagedMagentoOperation(environmentId, op);
      setConfirm(null);
      onExecutionStarted(res.execution.id);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      setFailed(op);
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  }

  if (view.loading && !view.data) {
    return (
      <Section title="Magento operations" testId="magento-operations">
        <LoadingRows label="Reading the indexers" rows={4} rowHeight="28px" />
      </Section>
    );
  }
  if (view.error) return <ErrorNotice error={view.error} onRetry={view.reload} />;
  const data = view.data;
  if (!data) return null;
  if (!data.available) {
    return (
      <Section title="Magento operations" testId="magento-operations">
        <Callout tone="info">{data.reason}</Callout>
      </Section>
    );
  }

  const indexers = data.indexers;
  return (
    <Section
      title="Magento operations"
      testId="magento-operations"
    >
      <ErrorNotice error={error} onRetry={failed ? () => void run(failed) : undefined} />

      <Subsection title="Indexers">
        <div className="magento-ops__row" data-testid="magento-indexers">
          {!indexers || !indexers.known ? (
            <p data-testid="magento-indexers-unknown">
              <LevelBadge level="unknown">Not known</LevelBadge>{' '}
              <span className="muted">{indexers?.reason ?? 'The indexers could not be read.'}</span>{' '}
              <button type="button" className="link-button" onClick={view.reload}>
                Retry
              </button>
            </p>
          ) : indexers.valid ? (
            <p>
              <LevelBadge level="ok">All valid</LevelBadge>{' '}
              <span className="muted">
                {Object.keys(indexers.statuses ?? {}).length} indexers, checked {relativeTime(indexers.checked_at)}
              </span>
            </p>
          ) : (
            <p>
              <LevelBadge level="critical">{indexers.invalid.length} need a reindex</LevelBadge>{' '}
              <span>{indexers.invalid.map((id) => <code key={id} className="magento-ops__id">{id}</code>)}</span>
            </p>
          )}
          <button type="button" className="button button--secondary button--small" onClick={() => setConfirm('reindex_all')} disabled={busy}>
            Reindex all
          </button>
        </div>
      </Subsection>

      <Subsection title="Cron">
        <Checks checks={data.cron} empty="No cron check" />
      </Subsection>

      <Subsection title="Queue consumers">
        <Checks checks={data.consumers} empty="No consumer check" />
      </Subsection>

      <Subsection title="Cache">
        <div className="magento-ops__row">
          <p className="muted">Clean removes outdated entries; flush empties the whole cache storage.</p>
          <span className="pill-list">
            <button type="button" className="button button--secondary button--small" onClick={() => setConfirm('cache_clean')} disabled={busy}>
              Clean cache
            </button>
            <button type="button" className="button button--secondary button--small" onClick={() => setConfirm('cache_flush')} disabled={busy}>
              Flush cache
            </button>
          </span>
        </div>
      </Subsection>

      {confirm ? (
        <ConfirmDialog
          open
          title={operationText[confirm].title}
          description={operationText[confirm].description}
          confirmLabel={operationText[confirm].label}
          busy={busy}
          onConfirm={() => run(confirm)}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </Section>
  );
}

function Checks({ checks, empty }: { checks: MagentoOperationCheck[]; empty: string }) {
  if (checks.length === 0) return <QuietEmpty>{empty}</QuietEmpty>;
  return (
    <ul className="status-list">
      {checks.map((check) => (
        <li key={check.monitor_id} className="status-list__item">
          <span className="status-list__name">{check.name}</span>
          <span className="status-list__detail muted">
            {check.failing_since
              ? `failing since ${sinceWords(check.failing_since)}, ${check.consecutive_failures} ${check.consecutive_failures === 1 ? 'check' : 'checks'} in a row`
              : check.last_run_at
                ? `checked ${relativeTime(check.last_run_at)}`
                : 'not run yet'}
          </span>
          <StatusBadge kind="check" value={check.status} />
        </li>
      ))}
    </ul>
  );
}
